import {
  enumRouteStatus,
  enumRouteSortOption,
} from '@rematter/frontend-lib-api-client';

import { RouteRecord } from '~/modules/transportation/types';
import { createQueryHook } from '~/shared/providers/api';
import {
  maybeParseZonedDateTime,
  parseZonedDateTime,
} from '~/shared/utils/dateTime';

export const useRoutesQuery = createQueryHook(
  'transportation.routes',
  (params: {
    ids?: string[];
    status?: (keyof typeof enumRouteStatus)[];
    searchTerm?: string;
    limit?: number;
    offset?: number;
    sorting?: (keyof typeof enumRouteSortOption)[];
  }) => ({
    routes: {
      __args: {
        filter: {
          ids: params.ids,
          status: params.status,
          searchTerm: params.searchTerm,
        },
        sorting: params.sorting,
        pagination: {
          limit: params.limit ?? 10,
          offset: params.offset ?? 0,
        },
      },
      entries: {
        id: true,
        description: true,
        status: true,
        documentNumber: true,
        routeDate: true,
        startedAt: true,
        completedAt: true,
        driverId: true,
        haulerId: true,
        truckId: true,
        trailerId: true,
        facilityGroupId: true,
        driver: {
          id: true,
          firstName: true,
          lastName: true,
        },
        services: {
          id: true,
          routePosition: true,
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): { routes: RouteRecord[]; total?: number } => ({
      routes:
        data?.routes.entries.map((route) => ({
          id: route.id,
          description: route.description,
          status: route.status,
          documentNumber: route.documentNumber,
          routeDate: maybeParseZonedDateTime(route.routeDate),
          startedAt: maybeParseZonedDateTime(route.startedAt),
          completedAt: maybeParseZonedDateTime(route.completedAt),
          driverId: route.driverId ?? undefined,
          haulerId: route.haulerId ?? undefined,
          truckId: route.truckId ?? undefined,
          trailerId: route.trailerId ?? undefined,
          facilityGroupId: route.facilityGroupId,
          driver: route.driver ?? undefined,
          services: (route.services ?? [])
            .filter((service) => service.routePosition != null)
            .map((service) => ({
              id: service.id,
              routePosition: service.routePosition as number,
            })),
          timestamps: {
            createdAt: parseZonedDateTime(route.timestamps.createdAt),
            updatedAt: parseZonedDateTime(route.timestamps.updatedAt),
          },
        })) ?? [],
      total: data?.routes.page.total ?? undefined,
    }),
  },
);
